/**
 * LT-16 Part 2a — Tender Details edit form.
 *
 * The blocker: there was no way to edit a tender's own details
 * after creation. A user who picked the wrong job type had to
 * delete the tender and start again. Every field on this form
 * maps directly to what `TenderUpdate` on the backend accepts;
 * PATCH /api/tenders/{id} does the writes.
 *
 * Two decisions the brief left to the implementer:
 *
 *   • On job_type change with an existing schedule / A&O:
 *     - Milestone template: warn plainly. Offer to re-snapshot
 *       from the new job type's template. If the user declines,
 *       keep the existing snapshot and set a `stale_source_job_type`
 *       flag on it — the Payment Schedule tab renders a persistent
 *       warning naming the mismatch.
 *     - A&O template: the user's own edits are precious; the
 *       auto-tick template is just seed. We re-run auto-tick on
 *       the new job type — items matching the new type get seeded,
 *       manually-added items and manually-modified items are
 *       untouched. Ticks that came ONLY from the old-job auto-tick
 *       (no manual override, no modifications since) are un-seeded.
 *       Communicated with the user as "A&O auto-ticks will refresh".
 *
 *   • Brand change: the header brand chip updates immediately. Any
 *     previously-issued version's stored PDF continues to display
 *     whatever brand was in force at that snapshot.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save, Loader2 } from "lucide-react";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, PrimaryButton, QuietButton, Select, ToggleGroup,
} from "../../components/UI";
import { useJobTypeLibrary } from "../../hooks/useJobTypeLibrary";
import { useEnabledBrands } from "../../hooks/useEnabledBrands";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const dirtyOf = (form, original) => {
  const dirty = {};
  for (const k of Object.keys(form)) {
    // Coerce null / "" / undefined to the same class so an unchanged
    // empty field doesn't get PATCHed and doesn't count as dirty.
    const a = form[k] ?? "";
    const b = original[k] ?? "";
    if (a !== b) dirty[k] = form[k];
  }
  return dirty;
};

export function TenderDetailsTab({ tender, onTenderUpdate }) {
  const { activeTypes, loading: jobTypesLoading, labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  const { activeBrands, showPicker: showBrandPicker } = useEnabledBrands();
  const readOnly = tender?.status === "submitted";

  const original = useMemo(() => ({
    client_name:         tender?.client_name || "",
    client_email:        tender?.client_email || "",
    client_phone:        tender?.client_phone || "",
    site_address_line_1: tender?.site_address_line_1 || "",
    site_address_line_2: tender?.site_address_line_2 || "",
    site_city:           tender?.site_city || "",
    site_postcode:       tender?.site_postcode || "",
    brand:               tender?.brand || "lux_developments",
    job_type:            tender?.job_type || "",
    estimated_value:     tender?.estimated_value ?? 0,
  }), [tender]);

  const [form, setForm] = useState(original);
  const [saving, setSaving] = useState(false);
  const [jobTypeChangeDialog, setJobTypeChangeDialog] = useState(null);

  useEffect(() => { setForm(original); }, [original]);

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: k === "site_postcode" ? String(v || "").toUpperCase() : v }));

  const dirty = dirtyOf(form, original);
  const isDirty = Object.keys(dirty).length > 0;

  // Options
  const jobOptions = useMemo(() => {
    const list = activeTypes.map((j) => ({ value: j.key, label: j.label }));
    // Include the current job_type even if archived (labelFor resolves
    // archived keys) — never blank a user's own tender data.
    if (form.job_type && !list.find((o) => o.value === form.job_type)) {
      list.unshift({ value: form.job_type, label: `${jobTypeLabelFor(form.job_type)} (archived)` });
    }
    return list;
  }, [activeTypes, form.job_type, jobTypeLabelFor]);

  const brandOptions = useMemo(() => {
    const opts = activeBrands.map((k) => ({
      value: k,
      label: k === "lux_construction" ? "Lux Construction" : "Lux Developments",
    }));
    // Same rule as job type — never drop the current tender's brand
    // from the picker even if it's since been disabled.
    if (form.brand && !opts.find((o) => o.value === form.brand)) {
      opts.push({
        value: form.brand,
        label: `${form.brand === "lux_construction" ? "Lux Construction" : "Lux Developments"} (disabled)`,
      });
    }
    return opts;
  }, [activeBrands, form.brand]);

  const commit = async (patch, extra = {}) => {
    setSaving(true);
    try {
      const r = await api.patch(`/tenders/${tender.id}`, patch);
      onTenderUpdate?.(r.data);
      toast.success("Tender details saved");
      // Job-type-side effects.
      if ("job_type" in patch && extra.reSnapshotSchedule) {
        try {
          await api.post(`/tenders/${tender.id}/payment-schedule/re-snapshot`);
          toast.success("Payment schedule re-snapshotted from the new job type");
        } catch (e) {
          const msg = e?.response?.data?.detail;
          if (msg) toast.error(msg);
        }
      }
      if ("job_type" in patch && extra.reRunAO) {
        try {
          await api.post(`/tenders/${tender.id}/ao/recalculate-job-type-template`);
          toast.success("A&O auto-tick re-run");
        } catch { /* non-blocking */ }
      }
      setJobTypeChangeDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (readOnly) {
      toast.info("This tender is submitted (read-only). Click Revise on the tender to edit.");
      return;
    }
    if (!isDirty) return;
    const jobTypeChanged = "job_type" in dirty;
    if (jobTypeChanged) {
      // Show the warn-and-confirm dialog before saving — user chooses
      // whether to re-snapshot the schedule and re-run A&O auto-tick.
      setJobTypeChangeDialog({
        from: original.job_type,
        to: dirty.job_type,
        patch: dirty,
      });
      return;
    }
    await commit(dirty);
  };

  return (
    <div className="mt-6" data-testid="tender-details-tab">
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
              Tender details
            </h3>
            <p className="text-ink-3 mt-1 max-w-2xl" style={{ fontSize: 12 }}>
              The tender record itself — client, site address, brand, job type. Editing here
              writes to the tender document and, where relevant, prompts before touching downstream snapshots.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setForm(original)}
            disabled={!isDirty}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 12 }}
            data-testid="tender-details-reset-btn"
          >
            <RefreshCcw size={12} /> Reset
          </button>
        </div>

        {readOnly && (
          <div className="mb-5 rounded-md border border-note-amber/40 bg-note-amber/10 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-note-amber" />
            <div className="text-ink-2" style={{ fontSize: 12 }}>
              This tender is <strong>Submitted</strong> (read-only). Click <strong>Revise</strong> at
              the top of the tender to unlock editing. Any change re-opens the tender to Working;
              the sent version stays untouched in history.
            </div>
          </div>
        )}

        <form onSubmit={submit}>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Job type" required data-testid="tender-details-job-type-field">
              <Select
                value={form.job_type}
                onChange={set("job_type")}
                options={jobOptions}
                placeholder={jobTypesLoading ? "Loading…" : "Select a job type…"}
                disabled={readOnly}
              />
            </Field>
            {showBrandPicker ? (
              <Field label="Brand" required>
                <ToggleGroup
                  value={form.brand}
                  onChange={set("brand")}
                  options={brandOptions}
                />
              </Field>
            ) : <div />}

            <Field label="Client name" required>
              <Input value={form.client_name} onChange={set("client_name")} disabled={readOnly} />
            </Field>
            <div />

            <Field label="Client email">
              <Input type="email" value={form.client_email} onChange={set("client_email")} disabled={readOnly} />
            </Field>
            <Field label="Client phone">
              <Input value={form.client_phone} onChange={set("client_phone")} disabled={readOnly} />
            </Field>

            <Field label="Site address line 1" required>
              <Input value={form.site_address_line_1} onChange={set("site_address_line_1")} disabled={readOnly} />
            </Field>
            <Field label="Site address line 2">
              <Input value={form.site_address_line_2} onChange={set("site_address_line_2")} disabled={readOnly} />
            </Field>

            <Field label="Town / City">
              <Input value={form.site_city} onChange={set("site_city")} disabled={readOnly} />
            </Field>
            <Field label="Postcode" required>
              <Input mono value={form.site_postcode} onChange={set("site_postcode")} disabled={readOnly} />
            </Field>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            {isDirty && !readOnly && (
              <span className="text-note-amber" style={{ fontSize: 11 }}>Unsaved changes</span>
            )}
            <PrimaryButton
              type="submit"
              disabled={!isDirty || saving || readOnly}
              icon={saving ? Loader2 : Save}
              data-testid="tender-details-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </form>
      </Card>

      {/* LT-16 Part 2a — job type change confirm dialog. Names the
          downstream effects rather than surfacing them post-save as
          silent mutations. */}
      <AlertDialog open={!!jobTypeChangeDialog} onOpenChange={(v) => !v && setJobTypeChangeDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change job type on this tender?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing job type from{" "}
              <strong>{jobTypeLabelFor(jobTypeChangeDialog?.from) || "—"}</strong> to{" "}
              <strong>{jobTypeLabelFor(jobTypeChangeDialog?.to) || "—"}</strong>{" "}
              affects two downstream snapshots. Choose what happens to each:
              <ul className="mt-3 space-y-2 text-ink-2" style={{ fontSize: 12 }}>
                <li>
                  <strong>Payment schedule</strong>: the existing snapshot was built from the
                  old job type. Re-snapshotting rebuilds it from the new job type's
                  milestone template (allocations reset). Declining keeps the current
                  schedule and flags it as built-from-wrong-job-type on the Payment Schedule tab.
                </li>
                <li>
                  <strong>A&O auto-ticks</strong>: your own edits are preserved. Auto-tick
                  will re-run against the new job type — items matching the new type
                  get seeded; ticks that came only from the old auto-tick and haven't
                  been touched since are un-seeded. Manually-added / modified items
                  are never changed by this.
                </li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-type-change-cancel">Cancel</AlertDialogCancel>
            <button
              type="button"
              onClick={() => commit(jobTypeChangeDialog.patch, { reSnapshotSchedule: false, reRunAO: true })}
              className="inline-flex items-center h-9 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12.5 }}
              data-testid="job-type-change-keep-schedule"
            >
              Keep existing schedule
            </button>
            <AlertDialogAction
              onClick={() => commit(jobTypeChangeDialog.patch, { reSnapshotSchedule: true, reRunAO: true })}
              data-testid="job-type-change-resnapshot"
            >
              Re-snapshot schedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
